import { getLastQueryParamCaseInsensitive, httpUtilityUrlEncode } from '@ngsos/portal-core/incidents';
import type { RequestedIncidentDetails, RequestedIncidentLocation } from '@ngsos/portal-core/commands';

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export type CreateIncidentDialogModel = RequestedIncidentDetails & {
  requestedIncidentId?: string;
  phoneNumber: string;
};

function parseOptionalText(value: string | null): string | undefined {
  const trimmed = value?.trim();
  return trimmed === undefined || trimmed === '' ? undefined : trimmed;
}

function parseCoordinate(value: string | null): number | undefined {
  const trimmed = value?.trim();
  if (trimmed === undefined || trimmed === '') {
    return undefined;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function parseRequestedIncidentId(value: string | null): string | undefined {
  if (value === null || !GUID_PATTERN.test(value) || value.toLowerCase() === EMPTY_GUID) {
    return undefined;
  }

  return value.toLowerCase();
}

function parseLocation(searchParams: URLSearchParams): RequestedIncidentLocation | undefined {
  const latitude = parseCoordinate(getLastQueryParamCaseInsensitive(searchParams, 'Location.Latitude'));
  const longitude = parseCoordinate(getLastQueryParamCaseInsensitive(searchParams, 'Location.Longitude'));
  const capturedAtUtc = parseOptionalText(
    getLastQueryParamCaseInsensitive(searchParams, 'Location.CapturedAtUtc'),
  );

  return latitude === undefined || longitude === undefined || capturedAtUtc === undefined
    ? undefined
    : { source: 'Entered', latitude, longitude, capturedAtUtc };
}

function queryParameter(name: string, value: string): string {
  return `${name}=${httpUtilityUrlEncode(value)}`;
}

export function parseCreateIncidentDialogModel(searchParams: URLSearchParams): CreateIncidentDialogModel {
  return {
    requestedIncidentId: parseRequestedIncidentId(
      getLastQueryParamCaseInsensitive(searchParams, 'RequestedIncidentId'),
    ),
    phoneNumber: getLastQueryParamCaseInsensitive(searchParams, 'phoneNumber') ?? '',
    callerName: parseOptionalText(getLastQueryParamCaseInsensitive(searchParams, 'CallerName')),
    label: parseOptionalText(getLastQueryParamCaseInsensitive(searchParams, 'Label')),
    summary: parseOptionalText(getLastQueryParamCaseInsensitive(searchParams, 'Summary')),
    location: parseLocation(searchParams),
  };
}

export function buildIncidentListOpenNewIncidentDialogUrl(
  searchParams: URLSearchParams,
  newGuid: () => string = () => crypto.randomUUID(),
): string {
  const model = parseCreateIncidentDialogModel(searchParams);
  const requestedIncidentId = model.requestedIncidentId ?? newGuid();
  const query = [
    queryParameter('requestedIncidentId', requestedIncidentId),
    queryParameter('phoneNumber', model.phoneNumber),
  ];

  if (model.callerName !== undefined) {
    query.push(queryParameter('callerName', model.callerName));
  }
  if (model.label !== undefined) {
    query.push(queryParameter('label', model.label));
  }
  if (model.summary !== undefined) {
    query.push(queryParameter('summary', model.summary));
  }
  if (model.location !== undefined) {
    query.push(queryParameter('location.latitude', String(model.location.latitude)));
    query.push(queryParameter('location.longitude', String(model.location.longitude)));
    query.push(queryParameter('location.capturedAtUtc', model.location.capturedAtUtc));
  }

  return `/incident-list?${query.join('&')}`;
}
